import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useAgreementCorrectionPrecheckQuery,
  useCorrectAgreementGroupActionMutation,
  useUndoEnrollmentActionMutation,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';
import { CORRECTION_NS } from './agreementCorrectionModel';

/** Результат предпроверки в том виде, в каком его рисует экран. */
export interface IAgreementCorrectionCheck {
  canCorrect: boolean;
  canUndo: boolean;
  correctBlockers: string[];
  undoBlockers: string[];
  warnings: string[];
  /** ISO-строка; `null` — договор заведён до учёта даты создания. */
  undoDeadline: string | null;
}

interface IUseAgreementCorrectionCheck {
  check: IAgreementCorrectionCheck | null;
  loading: boolean;
  error?: ApolloError;
  /**
   * Код отказа, которым сервер ответил ошибкой (`AGREEMENT_NOT_FOUND`), —
   * чтобы экран перевёл его тем же словарём, что и блокеры.
   */
  errorCode: string | null;
  refetch: () => void;
}

const CODE_RE = /^[A-Z][A-Z_]+$/;

/**
 * Смонтирован ли ещё вызывающий. После отмены зачисления строка ростера (а с
 * ней и окно) исчезает раньше, чем мутация вернёт управление, — снимать
 * «загрузку» у размонтированного компонента не нужно.
 */
const useAlive = () => {
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  return alive;
};

/**
 * ERP-110 — «можно ли исправить группу / отменить зачисление».
 *
 * `network-only`: блокеры зависят от живого состояния (фактура могла
 * появиться минуту назад), поэтому каждое открытие окна спрашивает сервер.
 * `nextFetchPolicy: 'cache-first'` — после первого ответа: иначе Apollo 3.3
 * перезапрашивал базовую проверку всякий раз, когда в кеш писался ответ
 * проверки выбранной группы (замер на стенде: лишний запрос на каждый выбор
 * группы). `refetch()` по-прежнему идёт в сеть.
 * `targetGroupId` — выбранная группа: без неё сервер возвращает только то,
 * что от группы не зависит. `skip` — ещё нечего спрашивать (группа не
 * выбрана).
 */
export const useAgreementCorrectionCheck = (
  agreementId: string,
  targetGroupId: string | null,
  options: { skip?: boolean } = {},
): IUseAgreementCorrectionCheck => {
  const { data, loading, error, refetch } = useAgreementCorrectionPrecheckQuery(
    {
      variables: { agreementId, targetGroupId },
      skip: !agreementId || Boolean(options.skip),
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    },
  );

  const raw = data?.agreementCorrectionCheck;
  const check = useMemo<IAgreementCorrectionCheck | null>(
    () =>
      raw
        ? {
            canCorrect: raw.canCorrect,
            canUndo: raw.canUndo,
            correctBlockers: [...raw.correctBlockers],
            undoBlockers: [...raw.undoBlockers],
            warnings: [...raw.warnings],
            undoDeadline: raw.undoDeadline ?? null,
          }
        : null,
    [raw],
  );

  const errorCode = useMemo(() => {
    const message = error?.graphQLErrors?.[0]?.message ?? '';
    return CODE_RE.test(message) ? message : null;
  }, [error]);

  const stableRefetch = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  return { check, loading, error, errorCode, refetch: stableRefetch };
};

type CustomErrors =
  | ReadonlyArray<{ code?: string | null; field?: string | null } | null>
  | null
  | undefined;

/** Коды отказа из `customErrors` — по записи на код, пустые отброшены. */
const codesOf = (errors: CustomErrors): string[] =>
  (errors ?? [])
    .map((e) => e?.code || '')
    .filter((code): code is string => Boolean(code));

export interface ICorrectAgreementGroupArgs {
  agreementId: string;
  groupId: string;
  reason: string;
}

/** Общее у итогов обеих мутаций. */
interface IMutationOutcome {
  ok: boolean;
  /** Коды отказа сервера; при сетевой ошибке пусто, текст — в `failure`. */
  codes: string[];
  failure?: string;
  /**
   * Сервер ответил `success=false`, не назвав ни одного кода, — экрану нечего
   * перевести, он показывает общую фразу «попробуйте ещё раз».
   */
  generic?: boolean;
  /** Повторный вызов, пока идёт первый: ничего не отправлено, показывать нечего. */
  skipped?: boolean;
}

/** Итог отказа: коды сервера или пометка, что их нет. */
const refusal = (errors: CustomErrors): IMutationOutcome => {
  const codes = codesOf(errors);
  return codes.length
    ? { ok: false, codes }
    : { ok: false, codes, generic: true };
};

export interface ICorrectAgreementGroupResult extends IMutationOutcome {
  groupName?: string | null;
  /** Номер договора до и после правки (ответ сервера). */
  oldTitle?: string | null;
  newTitle?: string | null;
  titleChanged?: boolean;
  /** Почему номер сохранён: SIGNED_DOCUMENT | MANUAL_TITLE | NAME_TOO_LONG. */
  titleKeptReason?: string | null;
}

/**
 * «Исправить группу» — правка на месте. Ответ обновляет в кеше только номер
 * договора; новую группу карточка и ростер получают своим `refetch`, который
 * вызывающий зовёт при закрытии окна (`onCorrected`).
 *
 * Повторный вызов, пока идёт первый, отбрасывается (`inFlight`), — кнопка
 * тоже гаснет, но двойной клик успевает раньше перерисовки.
 */
export const useCorrectAgreementGroup = () => {
  const [run] = useCorrectAgreementGroupActionMutation();
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  const alive = useAlive();

  const correct = useCallback(
    async (
      args: ICorrectAgreementGroupArgs,
    ): Promise<ICorrectAgreementGroupResult> => {
      if (inFlight.current) return { ok: false, codes: [], skipped: true };
      inFlight.current = true;
      setLoading(true);
      try {
        const result = await run({ variables: args });
        const payload = result.data?.correctAgreementGroup;
        if (payload?.success) {
          return {
            ok: true,
            codes: [],
            // Группу ответ не несёт (см. операцию) — имя знает окно.
            groupName: null,
            oldTitle: payload.oldTitle ?? null,
            newTitle: payload.newTitle ?? payload.agreement?.agreementTitle,
            titleChanged: Boolean(payload.titleChanged),
            titleKeptReason: payload.titleKeptReason ?? null,
          };
        }
        return refusal(payload?.customErrors);
      } catch (error) {
        return { ok: false, codes: [], failure: (error as Error).message };
      } finally {
        inFlight.current = false;
        if (alive.current) setLoading(false);
      }
    },
    [run, alive],
  );

  return { correct, loading };
};

export interface IUndoEnrollmentResult extends IMutationOutcome {
  studentId?: string | null;
  /** `false` — студент пропал из ERP (договоров юрлица не осталось). */
  studentVisible?: boolean;
}

/**
 * «Отменить зачисление» — удаление ошибочного договора.
 *
 * Кеш: договор удалён на сервере, поэтому его сущность выселяется из кеша
 * (`AgreementType:<id>`), — списки, которые на неё ссылались (карточка
 * студента, ростер группы), перестают её показывать сразу. Если студент
 * пропал из ERP совсем, выселяются и закешированные страницы списка
 * студентов (`allStudents` читается `cache-first` — иначе он показал бы
 * человека, которого больше не открыть). Без `refetchQueries` по имени.
 *
 * Порядок важен: сначала `onSuccess` вызывающего (закрыть окно, увести в
 * список, перечитать карточку), и только потом выселение. Выселение убирает
 * договор из списка — а с ним размонтирует и строку/карточку, внутри которой
 * живёт окно; сделай его раньше, и вызывающий доделывал бы работу уже
 * размонтированным.
 */
export const useUndoEnrollment = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const apollo = useApolloClient();
  const [run] = useUndoEnrollmentActionMutation();
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  const alive = useAlive();

  const undo = useCallback(
    async (
      args: { agreementId: string; reason: string },
      onSuccess?: (outcome: IUndoEnrollmentResult) => void,
    ): Promise<IUndoEnrollmentResult> => {
      if (inFlight.current) return { ok: false, codes: [], skipped: true };
      inFlight.current = true;
      setLoading(true);
      try {
        const result = await run({ variables: args });
        const payload = result.data?.undoEnrollment;
        if (!payload?.success) return refusal(payload?.customErrors);
        const studentVisible = payload.studentVisible !== false;
        const outcome: IUndoEnrollmentResult = {
          ok: true,
          codes: [],
          studentId: payload.studentId ?? null,
          studentVisible,
        };
        addToast({
          data: {
            type: ToastTypes.success,
            title: t(`${CORRECTION_NS}.undo.toastDone`),
            text: studentVisible
              ? undefined
              : t(`${CORRECTION_NS}.undo.toastStudentGone`),
          },
          type: GlobalToastTypeNames.StudentAgreementEdit,
        });
        onSuccess?.(outcome);

        const { cache } = apollo;
        cache.evict({
          id: cache.identify({
            __typename: 'AgreementType',
            id: args.agreementId,
          }),
        });
        if (!studentVisible) {
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'allStudents' });
        }
        cache.gc();
        return outcome;
      } catch (error) {
        return { ok: false, codes: [], failure: (error as Error).message };
      } finally {
        inFlight.current = false;
        if (alive.current) setLoading(false);
      }
    },
    [run, addToast, t, alive, apollo],
  );

  return { undo, loading };
};
